import React, { useState, useEffect, forwardRef, useContext } from "react";
import BranchList from "./BranchList";
import { getBranches } from "./services/BranchLocatorService";
import classNames from "classnames";
import { APIProvider } from "@vis.gl/react-google-maps";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";
import BranchSearch from "../Map/BranchSearch";
import Map from "../Map"
import Marker from "../Map/Marker";
import styled from "styled-components";
import { LoadingTab } from "../BranchLocator/BranchExpander";
import HeaderHeightContext from "../../context/HeaderContext";
import { Branch } from "./models/Branch";
import { useIsMobile } from "../../context/MobileContext";
import { Button } from "react-bootstrap";
import { BRANCH_TYPE } from "../../utilities/enums";
import IGeoPosition from "components/WealthAdvisorLookup/models/IGeoPosition";
import useLanguage from "../../utilities/useLanguage";
import { getTranslation } from "./translations";
import googleConfig from "../Map/google-config";

interface IBranchError {
    header: string;
    subHeader?: string;
}

const MapContainer = styled.div`
    border-bottom: 3px solid ${props => props.theme.secondary};

    @media(min-width:992px) {
        position: -webkit-sticky !important;
        position: -moz-sticky !important;
        position: -ms-sticky !important;
        position: -o-sticky !important;
        position: sticky !important;
        top: 0;
        z-index: 999;
    }
`;

export interface IBranchLocatorProps extends IEditableProps, IStyledProps { }

const BranchLocator = forwardRef((props: IBranchLocatorProps, ref: any) => {
    const { lang } = useLanguage();
    const translations = getTranslation(lang);
    const limit = 7;
    const defaultLat = 39.809734;
    const defaultRadius = 25;
    const defaultLng = -98.555620;
    const [latLng, setLatLng] = useState({ lat: undefined, lng: undefined })
    const [mapPoint, setMapPoint] = useState({ lat: defaultLat, lng: defaultLng });
    const [loadingTabPartnerAtmClicked, setLoadingTabPartnerAtmClicked] = useState(false);
    const [page, setPage] = useState(1);
    const [radius, setRadius] = useState(defaultRadius);
    const [type, setType] = useState(1);
    const [value, setValue] = useState("");
    const [error, setError] = useState<IBranchError|null>({ header: translations.branchTypeErrorDefaultHeader });
    const [focusOnInput, setFocusOnInput] = useState(false);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [showMore, setMoreResults] = useState(true);
    const [zoom, setZoom] = useState(4);
    const [userLocation, setUserLocation] = useState<IGeoPosition|null>(null);
    const headerHeight = useContext(HeaderHeightContext);
    const { isMobileWidth } = useIsMobile();
    const [panTo, setPanTo] = useState(null);
    const [selectedFromMarker, setSelectedFromMarker] = useState(true);
    const [itemIconClass, setItemIconClass] = useState("");
    const [searchFieldsChanged, setSearchFieldsChanged] = useState(false);

    useEffect(() => {
        if (window) {
            const url = new URL(window.location.href).searchParams;
            const radParam = Number(url.get("rad"));
            const typeParam = Number(url.get("type"));
            const searchParam = url.get("search");
            const skipLocation = url.get("skipgeolocation");
            if (radParam && searchParam && typeParam) {
                setRadius(radParam);
                setType(typeParam);
                setValue(searchParam);
            } else if (!skipLocation) {
                tryGeolocation();
            }

        } else {
            console.error("no window is available")
        }

    }, []);

    useEffect(() => {
        if (loadingTabPartnerAtmClicked) {
            getResults();
            setLoadingTabPartnerAtmClicked(false);
        }
    }, [loadingTabPartnerAtmClicked]);

    function setUrlParams() {
        const branchLocationsPage = typeof document !== 'undefined' ? document.location.pathname : '/branch-locations';
        const urlStr = `${branchLocationsPage}?search=${encodeURIComponent(value)}&rad=${radius}&type=${type}`;
        window.history.replaceState({}, "", urlStr);
    }

    function tryGeolocation() {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(function (position) {
                setLatLng({ lat: position.coords.latitude, lng: position.coords.longitude });
                setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
            });
        }
    }

    useEffect(() => {
        if (isMobileWidth) {
            setRadius(defaultRadius);
        }
    }, [isMobileWidth]);

    useEffect(() => {
        if (latLng.lng && latLng.lat && radius && type && page > 1) {
            getSearchResults();
        }
    }, [page]);

    // Something changed that requires a new search
    useEffect(() => {
        if (latLng && radius && type && searchFieldsChanged) {
            setPage(1);
            getResults();
        }
    }, [latLng]);

    async function getResults() {
        try {
            setError(null);
            setResults([]);
            setUrlParams();

            if (latLng.lng && latLng.lat) {
                setLoading(true);
                const branches: Branch[] = await getBranches({ latLng, radius, limit, page: 1, type });

                if (branches.length) {
                    if (userLocation) {
                        setValue(branches[0].address_line_2);
                    }

                    setSelectedId(String(branches[0].id));
                    setResults(branches);
                    setIconClassByType();
                    setMapPoint(latLng);

                    if (branches.length === 1) {
                        setMapPoint({ lat: branches[0].latitude, lng: branches[0].longitude });
                        setZoom(12);
                    }

                } else {
                    setMapPoint(latLng);
                    setResults([]);
                    setZoom(8);
                    setErrorByType();
                }

                checkResultsLength(branches);
                setLoading(false);
                setSearchFieldsChanged(false);
            }

        } catch (err) {
            setErrorByType();
            setLoading(false);
        }
    }

    function setErrorByType() {
        switch (type) {
            case (BRANCH_TYPE.MACU):
                setError({ header: translations.branchTypeErrorBranchHeader });
                break;
            case (BRANCH_TYPE.MACU_ATM):
                setError({
                    header: translations.branchTypeErrorAtmHeader,
                    subHeader: translations.branchTypeErrorAtmSubHeader,
                });
                break;
            case (BRANCH_TYPE.MACU_SPANISH):
                    setError({
                        header: translations.branchTypeErrorSpanishHeader,
                        subHeader: translations.branchTypeErrorSpanishSubHeader,
                    });
                    break;
            default:
                setError({
                    header: translations.branchTypeErrorPartnerHeader,
                    subHeader: translations.branchTypeErrorPartnerSubHeader,
                });
                break;
        }
    }

    // Appends results to current list
    async function getSearchResults() {
        try {
            setError(null);
            setLoading(true);
            if (latLng.lat && latLng.lng) {
                const branches: Branch[] = await getBranches({ latLng, radius, limit, page, type });

                if (branches.length) {
                    const mergedArray = [...results, ...branches];
                    const filtered = mergedArray.filter((item, index) => index === mergedArray.findIndex(newItem => newItem.id === item.id))

                    setResults(filtered);
                    setMapPoint(latLng);
                    setIconClassByType();
                }

                checkResultsLength(branches);
                setLoading(false);
            }

        } catch (err) {
            setErrorByType();
            setLoading(false);
        }
    }

    useEffect(() => {
        if (results.length) {
            setPanTo(null);
        }

    }, [results]);

    useEffect(() => {
        const branch = results.find(b => b.id == selectedId);
        if (branch) {
            if (!selectedFromMarker) {
                setPanTo({ lat: parseFloat(branch.latitude), lng: parseFloat(branch.longitude) });
            }
        }
    }, [selectedId]);

    function handleLoadMore() {
        setPage(page + 1);
    }

    function setIconClassByType() {
        if (type === BRANCH_TYPE.MACU_ATM || type === BRANCH_TYPE.PARTNER_ATM) {
            setItemIconClass("item-atm-icon");
        }
        else {
            setItemIconClass("item-title-icon");
        }
    }

    function checkResultsLength(branches) {
        if (branches.length === 0 || limit > branches.length) {
            setMoreResults(false);
        } else {
            setMoreResults(true);
        }
    }

    function handleMarkerSelect(id) {
        setSelectedFromMarker(true);
        setSelectedId(id);
    }

    function handleBranchSelect(id) {
        setPanTo(null);
        setSelectedFromMarker(false);
        setSelectedId(id);
    }

    async function handleSearch(lat, lng, radius, type, address) {
        setValue(address);
        setRadius(radius);
        setType(parseInt(type));
        setLatLng({ lat, lng });
    }

    function handleSearchChange() {
        setSearchFieldsChanged(true);
    }

    function handleLoadingTabClickOrBlur(focus) {
        if (focus) {
            if (type === BRANCH_TYPE.MACU_ATM && results.length === 0) {
                setType(BRANCH_TYPE.PARTNER_ATM);
                setPage(1);
                setLoadingTabPartnerAtmClicked(true);
            } else {
                setFocusOnInput(focus);
            }
        } else {
            setFocusOnInput(focus);
        }
    }

    return (
        <div data-id={props.cmsId} ref={ref} className={classNames("m-0 w-100", props.editorClassObj)}>
            <APIProvider
                apiKey={googleConfig.apiKey}
                language={lang}
                libraries={googleConfig.libraries}>
                <div className="row px-4 py-1 py-lg-0 justify-content-center">
                    <div className="mr-2 mb-0 col-12 p-0 d-block d-lg-none text-center">
                        <h3 className="text-primary h6">{translations.findABranch}</h3>
                        <Button type="button"
                            className="w-100 align-items-center text-center"
                            onClick={tryGeolocation}>{translations.useCurrentLocation}</Button>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 p-0">
                        <BranchSearch
                            loadMap={process.env.GATSBY_LOAD_MAP === "true"}
                            defaultValue={value}
                            defaultRadius={radius}
                            userLocation={userLocation}
                            defaultType={type}
                            focusOnInput={focusOnInput}
                            onChange={handleSearchChange}
                            onSearch={handleSearch}>
                        </BranchSearch>
                    </div>
                </div>
                <div className="d-inline-block w-100 px-md-0">
                    <MapContainer className={"col-12 col-lg-7 col-xl-8 p-0 float-left"}>
                        <Map excludeApi latitude={mapPoint.lat} longitude={mapPoint.lng} loadMap panTo={panTo} zoom={zoom} maxZoom={14}>
                            {results.length && results.map(result => {
                                let markerContent;
                                if (result.wait_time !== null && result.is_branch_open) {
                                    markerContent = (
                                    <>
                                        <span>{result.wait_time}</span>
                                        <span className="small">min</span>
                                        <span className="small">wait</span>
                                    </>
                                    );
                                }
                                return (
                                    <Marker
                                        type={type}
                                        key={result.id}
                                        selectedId={selectedId}
                                        onClick={handleMarkerSelect}
                                        id={result.id}
                                        latitude={result.latitude}
                                        longitude={result.longitude}
                                        markerContent= {markerContent}
                                    />
                                )
                            })}
                        </Map>
                    </MapContainer>
                    <div className="col-12 col-lg-5 col-xl-4 p-0 branch-location-list float-left">
                        {results.length ?
                            <BranchList
                                selectedId={selectedId}
                                results={results}
                                onLoadMore={handleLoadMore}
                                loading={loading}
                                type={type}
                                showMore={showMore}
                                loadMoreLabel={translations.loadMore}
                                onSelect={handleBranchSelect}
                                itemIconClass={itemIconClass}
                                lang={lang}
                            />
                            :
                            <LoadingTab showSpinner={loading} onClickOrBlur={handleLoadingTabClickOrBlur} label={error ? error : translations.branchTypeErrorDefaultHeader} />
                        }
                    </div>
                </div>
            </APIProvider>
        </div>
    );
});

export default BranchLocator;